import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SEO_CONFIG,
  generateSocialTags,
  generateStructuredData,
} from "../utils/seo";

const SEO = ({
  title,
  description,
  keywords = [],
  image,
  type = "website",
  structuredData,
  breadcrumbs,
  canonical,
  noindex = false,
  nofollow = false,
  article = null,
  course = null,
  quiz = null,
  children,
}) => {
  const location = useLocation();

  // Generate page title
  const pageTitle = title
    ? `${title} | ${SEO_CONFIG.siteName}`
    : SEO_CONFIG.defaultTitle;

  // Generate description
  const pageDescription = description || SEO_CONFIG.defaultDescription;

  // Generate keywords
  const pageKeywords = [...SEO_CONFIG.keywords, ...keywords].join(", ");

  // Generate canonical URL
  const canonicalUrl = canonical || `${SEO_CONFIG.siteUrl}${location.pathname}`;

  // Generate social media tags
  const socialTags = generateSocialTags({
    title: pageTitle,
    description: pageDescription,
    type,
    url: canonicalUrl,
    image,
  });

  // Generate structured data based on content type
  let jsonLd = null;
  if (structuredData) {
    jsonLd = structuredData;
  } else if (course) {
    jsonLd = generateStructuredData("Course", course);
  } else if (quiz) {
    jsonLd = generateStructuredData("Quiz", quiz);
  } else if (article) {
    jsonLd = generateStructuredData("Article", article);
  }

  // Generate breadcrumb structured data
  const breadcrumbData = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: crumb.name,
          item: `${SEO_CONFIG.siteUrl}${crumb.url}`,
        })),
      }
    : null;

  // Combine structured data
  const combinedStructuredData = [];
  if (jsonLd) combinedStructuredData.push(jsonLd);
  if (breadcrumbData) combinedStructuredData.push(breadcrumbData);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content={SEO_CONFIG.author} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content={SEO_CONFIG.language} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots Meta */}
      <meta
        name="robots"
        content={`${noindex ? "noindex" : "index"}, ${nofollow ? "nofollow" : "follow"}`}
      />

      {/* Open Graph and Twitter Tags */}
      {Object.entries(socialTags).map(([property, content]) => {
        if (property.startsWith("og:")) {
          return <meta key={property} property={property} content={content} />;
        } else if (property.startsWith("twitter:")) {
          return <meta key={property} name={property} content={content} />;
        } else {
          return <meta key={property} name={property} content={content} />;
        }
      })}

      {/* Additional Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />

      {/* Favicon and Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link rel="manifest" href="/manifest.json" />

      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />

      {/* Language Alternatives */}
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* RSS Feed */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title={`${SEO_CONFIG.siteName} RSS Feed`}
        href={`${SEO_CONFIG.siteUrl}/rss.xml`}
      />

      {/* Structured Data */}
      {combinedStructuredData.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(
            combinedStructuredData.length === 1
              ? combinedStructuredData[0]
              : combinedStructuredData,
          )}
        </script>
      )}

      {/* Article-specific meta tags */}
      {article && (
        <>
          <meta property="article:author" content={article.author} />
          <meta
            property="article:published_time"
            content={article.createdDate}
          />
          <meta
            property="article:modified_time"
            content={article.lastUpdated}
          />
          <meta property="article:section" content={article.category} />
          {article.tags &&
            article.tags.map((tag) => (
              <meta key={tag} property="article:tag" content={tag} />
            ))}
        </>
      )}

      {/* Educational content meta tags */}
      {(course || quiz || article) && (
        <>
          <meta
            name="educational-level"
            content={course?.level || quiz?.level || article?.level}
          />
          <meta
            name="educational-subject"
            content={course?.title || quiz?.subject || article?.subject}
          />
        </>
      )}

      {/* Custom meta tags passed as children */}
      {children}
    </Helmet>
  );
};

export default SEO;
